import pdfMake from "pdfmake/build/pdfmake.min";
import { pdfMake as pdfFonts } from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.vfs;

const number = (value) => Number(value || 0);
const money = (value) => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
}).format(number(value));

const date = (value) => value
  ? new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" }).format(new Date(value))
  : "Not specified";

const safeFilename = (value) => String(value || "Taleem").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");

export function buildFeeTypeCollectionPdf({ business, rows, totals, start, end }) {
  const organization = business && business.name ? business.name : "Taleem";
  const sortedRows = [...rows].sort((left, right) =>
    String(left.departmentName || "Department not assigned").localeCompare(String(right.departmentName || "Department not assigned"))
    || String(left.feeTypeName || "").localeCompare(String(right.feeTypeName || "")),
  );
  const generatedAt = new Intl.DateTimeFormat("en-US", {
    year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit",
  }).format(new Date());
  const collectionRate = number(totals.invoiced) > 0
    ? `${((number(totals.collected) / number(totals.invoiced)) * 100).toFixed(1)}%`
    : "0.0%";
  const body = [
    ["Fee type", "Department", "Invoices", "Parents", "Charges", "Invoiced", "Collected", "Merchant collected"].map((text) => ({ text, style: "tableHeader" })),
    ...sortedRows.map((row) => [
      { text: row.feeTypeName || "Unassigned", bold: true },
      { text: row.departmentName || "Department not assigned", color: "#52645a" },
      { text: String(number(row.invoiceCount)), alignment: "center" },
      { text: String(number(row.parentCount)), alignment: "center" },
      { text: String(number(row.chargeCount)), alignment: "center" },
      { text: money(row.invoicedAmount), alignment: "right" },
      { text: money(row.collectedAmount), alignment: "right", bold: true, color: "#2f8738" },
      { text: money(row.merchantCollectedAmount), alignment: "right", bold: true, color: "#285f8f" },
    ]),
    [
      { text: "TOTAL", bold: true, colSpan: 2 }, {},
      { text: String(number(totals.invoiceCount)), alignment: "center", bold: true },
      { text: String(number(totals.parentCount)), alignment: "center", bold: true },
      { text: String(number(totals.chargeCount)), alignment: "center", bold: true },
      { text: money(totals.invoiced), alignment: "right", bold: true },
      { text: money(totals.collected), alignment: "right", bold: true, color: "#2f8738" },
      { text: money(totals.merchantCollected), alignment: "right", bold: true, color: "#285f8f" },
    ],
  ];

  return {
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: [30, 32, 30, 40],
    defaultStyle: { font: "Roboto", fontSize: 8, color: "#26342c" },
    footer(currentPage, pageCount) {
      return {
        columns: [
          { text: organization, margin: [30, 0, 0, 0], color: "#78867d", fontSize: 7 },
          { text: `Confidential - Board report  |  Page ${currentPage} of ${pageCount}`, alignment: "right", margin: [0, 0, 30, 0], color: "#78867d", fontSize: 7 },
        ],
      };
    },
    content: [
      {
        columns: [
          [
            { text: organization, color: "#3f9f48", bold: true, fontSize: 12 },
            { text: "COLLECTION BY FEE TYPE", bold: true, fontSize: 21, margin: [0, 3, 0, 2] },
            { text: "School revenue summarized by fee type and department", color: "#65756b", fontSize: 9 },
          ],
          {
            width: 210,
            alignment: "right",
            stack: [
              { text: "REPORTING PERIOD", color: "#3f9f48", bold: true, fontSize: 8 },
              { text: `${date(start)} - ${date(end)}`, bold: true, fontSize: 11, margin: [0, 4, 0, 0] },
              { text: `Generated ${generatedAt}`, color: "#7b887f", fontSize: 7, margin: [0, 3, 0, 0] },
            ],
          },
        ],
        margin: [0, 0, 0, 16],
      },
      {
        columns: [
          { text: [{ text: money(totals.invoiced), style: "metricValue" }, "\nAmount invoiced"], style: "metric" },
          { text: [{ text: money(totals.collected), style: "metricValueGreen" }, "\nAmount collected"], style: "metric" },
          { text: [{ text: money(totals.merchantCollected), style: "metricValueBlue" }, "\nMerchant collected"], style: "metric" },
          { text: [{ text: String(number(totals.chargeCount)), style: "metricValue" }, "\nCharges created"], style: "metric" },
          { text: [{ text: collectionRate, style: "metricValue" }, "\nCollection rate"], style: "metric" },
        ],
        columnGap: 10,
        margin: [0, 0, 0, 16],
      },
      { text: "FEE TYPE DETAILS", bold: true, fontSize: 11, color: "#31463a", margin: [0, 0, 0, 6] },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: [170, 125, 42, 42, 42, 75, 75, 75],
          body,
        },
        layout: {
          fillColor(rowIndex) {
            if (rowIndex === 0) return "#3f9f48";
            if (rowIndex === body.length - 1) return "#e9f4ea";
            return rowIndex % 2 === 0 ? "#f5f8f6" : null;
          },
          hLineColor: "#dce5df",
          vLineColor: "#e6ece8",
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 6,
          paddingBottom: () => 6,
        },
      },
    ],
    styles: {
      tableHeader: { bold: true, color: "#ffffff", fontSize: 7, alignment: "center" },
      metric: { color: "#718077", fontSize: 8, margin: [10, 9, 10, 9], fillColor: "#f5f8f6" },
      metricValue: { bold: true, color: "#26342c", fontSize: 14 },
      metricValueGreen: { bold: true, color: "#2f8738", fontSize: 14 },
      metricValueBlue: { bold: true, color: "#285f8f", fontSize: 14 },
    },
  };
}

export function downloadFeeTypeCollectionPdf(payload) {
  const organization = payload.business && payload.business.name ? payload.business.name : "Taleem";
  const periodStart = payload.start ? new Date(payload.start).toISOString().slice(0, 10) : "report";
  const filename = `${safeFilename(organization)}-collection-by-fee-type-${periodStart}.pdf`;
  return new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(buildFeeTypeCollectionPdf(payload)).download(filename, resolve);
    } catch (error) {
      reject(error);
    }
  });
}
